import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { getContentByLocale, getMediaByLocale } from "../content";
import type { MediaItem, MediaType } from "../content";
import { useI18n } from "../i18n";
import { PageSeo } from "../components/seo/PageSeo";
import { MediaGrid } from "../components/media/MediaGrid";

const validTypes: MediaType[] = ["fotos", "videos"];
const ITEMS_PER_PAGE = 6;
const MEDIA_CACHE_TTL_MS = 5 * 60 * 1000;

type PaginatedMediaResponse = {
  items: Array<any>;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

type CachedMediaResponse = {
  payload: PaginatedMediaResponse;
  cachedAt: number;
};

const mediaPageCache = new Map<string, CachedMediaResponse>();
const mediaPageInFlight = new Map<string, Promise<PaginatedMediaResponse | null>>();

function normalizeMediaPayload(payload: PaginatedMediaResponse | any[]): PaginatedMediaResponse {
  const payloadItems = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
      ? payload.items
      : [];

  return {
    items: payloadItems,
    page: Array.isArray(payload) ? 1 : Number(payload?.page ?? 1),
    limit: Array.isArray(payload) ? ITEMS_PER_PAGE : Number(payload?.limit ?? ITEMS_PER_PAGE),
    total: Array.isArray(payload) ? payloadItems.length : Number(payload?.total ?? payloadItems.length),
    totalPages: Array.isArray(payload)
      ? 1
      : Number(payload?.totalPages ?? (payloadItems.length > 0 ? 1 : 0)),
  };
}

export function MediaPage() {
  const { type } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { locale } = useI18n();
  const content = getContentByLocale(locale);
  const [dynamicItems, setDynamicItems] = useState<MediaItem[]>([]);
  const initialPage = Math.max(1, Number(searchParams.get("page") ?? 1));
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [backendTotalPages, setBackendTotalPages] = useState(1);

  useEffect(() => {
    let cancelled = false;

    async function fetchMedia() {
      try {
        const endpoint = type === "fotos" ? "photos" : "videos";
        const cacheKey = `${endpoint}:${currentPage}:${ITEMS_PER_PAGE}`;
        const cached = mediaPageCache.get(cacheKey);
        const isCacheValid = cached && Date.now() - cached.cachedAt <= MEDIA_CACHE_TTL_MS;

        let normalizedPayload: PaginatedMediaResponse | null = isCacheValid ? cached.payload : null;

        if (!normalizedPayload) {
          let inFlight = mediaPageInFlight.get(cacheKey);
          if (!inFlight) {
            inFlight = (async () => {
              const response = await fetch(`/api/media/${endpoint}?page=${currentPage}&limit=${ITEMS_PER_PAGE}`);
              if (!response.ok) {
                return null;
              }

              const payload: PaginatedMediaResponse | any[] = await response.json();
              return normalizeMediaPayload(payload);
            })();
            mediaPageInFlight.set(cacheKey, inFlight);
          }

          normalizedPayload = await inFlight;
          mediaPageInFlight.delete(cacheKey);

          if (normalizedPayload) {
            mediaPageCache.set(cacheKey, {
              payload: normalizedPayload,
              cachedAt: Date.now(),
            });
          }
        }

        if (cancelled) {
          return;
        }

        if (normalizedPayload) {
          const normalizedData: MediaItem[] = normalizedPayload.items.map((item: any) => ({
            id: String(item.id ?? ""),
            type: item.type === "videos" ? "videos" : "fotos",
            title: String(item.title ?? ""),
            description: String(item.description ?? ""),
            thumbnail: String(item.thumbnail ?? ""),
            href: String(item.link ?? item.href ?? "#"),
          }));

          setDynamicItems(normalizedData);
          setBackendTotalPages(normalizedPayload.totalPages > 0 ? normalizedPayload.totalPages : 1);
        } else {
          setDynamicItems([]);
          setBackendTotalPages(1);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error(`Failed to fetch dynamic ${type}:`, error);
        setDynamicItems([]);
        setBackendTotalPages(1);
      }
    }

    if (type === "fotos" || type === "videos") {
      if (currentPage < 1) {
        setCurrentPage(1);
      }
      fetchMedia();
    }

    return () => {
      cancelled = true;
    };
  }, [type, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [type]);

  useEffect(() => {
    setSearchParams((params) => {
      params.set("page", String(currentPage));
      return params;
    }, { replace: true });
  }, [currentPage, setSearchParams]);

  if (!type || !validTypes.includes(type as MediaType)) {
    const invalidLabel = locale === "pt" ? "Tipo de mídia inválido." : "Invalid media type.";

    return (
      <section className="section info-panel">
        <p className="info-copy">{invalidLabel}</p>
        <Link className="hero-cta" to="/midia/fotos">
          {content.media.photosLabel}
        </Link>
      </section>
    );
  }

  const activeType = type as MediaType;
  const fallbackItems = getMediaByLocale(locale).filter(item => item.type === activeType);
  const items = dynamicItems.length > 0 ? dynamicItems : fallbackItems;

  const totalPages = dynamicItems.length > 0
    ? backendTotalPages
    : Math.max(1, Math.ceil(fallbackItems.length / ITEMS_PER_PAGE));

  const paginatedItems = useMemo(() => {
    const safePage = Math.min(Math.max(currentPage, 1), totalPages);
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    if (dynamicItems.length > 0) {
      return items;
    }

    return fallbackItems.slice(start, start + ITEMS_PER_PAGE);
  }, [currentPage, items, totalPages]);

  const prevLabel = locale === "pt" ? "Anterior" : "Previous";
  const nextLabel = locale === "pt" ? "Próxima" : "Next";
  const pageLabel = locale === "pt" ? "Página" : "Page";
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <>
      <PageSeo
        title={`${activeType === "fotos" ? content.media.photosLabel : content.media.videosLabel} | ${content.seo.defaultTitle}`}
        description={content.media.subtitle}
      />

      <section className="section media-page">
        <header className="media-header">
          <h1>{content.media.title}</h1>
          <p>{content.media.subtitle}</p>
          <div className="media-filters" role="tablist" aria-label={content.media.title}>
            <Link
              to="/midia/fotos"
              className={`media-filter${activeType === "fotos" ? " is-active" : ""}`}
            >
              {content.media.photosLabel}
            </Link>
            <Link
              to="/midia/videos"
              className={`media-filter${activeType === "videos" ? " is-active" : ""}`}
            >
              {content.media.videosLabel}
            </Link>
          </div>
        </header>

        <MediaGrid
          items={paginatedItems}
          openLabel={content.media.openLabel}
          emptyLabel={content.media.emptyLabel}
          getViewHref={(item) => `/midia/${activeType}/view/${encodeURIComponent(item.id)}?page=${currentPage}`}
        />

        {totalPages > 1 ? (
          <div className="media-pagination">
            <button
              type="button"
              className="media-page-btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              {prevLabel}
            </button>

            <div className="media-page-numbers" aria-label={pageLabel}>
              {pages.map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`media-page-btn media-page-number${page === currentPage ? " is-active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>

            <span className="media-page-label">
              {pageLabel} {Math.min(currentPage, totalPages)} / {totalPages}
            </span>

            <button
              type="button"
              className="media-page-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            >
              {nextLabel}
            </button>
          </div>
        ) : null}
      </section>
    </>
  );
}
